import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layers } from "lucide-react";
import { formatINR } from "../lib/format";

/**
 * Ladder Buy — a compact strip revealed on each Trading-Terminal card that
 * turns the current live-price tick into three quantity ladders (Sample,
 * Standard, Bulk). Tapping one opens the Request-a-Quote form pre-filled
 * with the material / quantity / unit — closes the "I saw the price, now
 * lock it" loop in a single click.
 */
export function LadderBuy({ category, brand, label, unit, price, color }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const material = useMemo(() => {
    if (category === "tmt") return "TMT Steel Bars";
    if (category === "cement") return "Cement";
    if (category === "structural" || category === "steel") return "Structural Steel";
    if (category === "plates") return "Plates & Sheets";
    if (category === "wire") return "Binding Wire";
    return "TMT Steel Bars";
  }, [category]);

  const grade = useMemo(() => {
    const b = (brand || "").toLowerCase();
    if (material === "TMT Steel Bars") {
      if (b.includes("550")) return "Fe 550D";
      if (b.includes("500d")) return "Fe 500D";
      return "Fe 500";
    }
    if (material === "Cement") {
      if (b.includes("53")) return "Grade 53";
      if (b.includes("ppc")) return "PPC";
      return "Grade 43";
    }
    return "";
  }, [material, brand]);

  const rungs = useMemo(() => {
    // Ladders scale by unit: MT for TMT/structural, bag for cement
    const isBag = (unit || "").toLowerCase().includes("bag");
    if (isBag) {
      return [
        { qty: 100, tag: "Sample" },
        { qty: 500, tag: "Standard" },
        { qty: 2000, tag: "Bulk" },
      ].map((r) => ({ ...r, unit: "bags", total: r.qty * price }));
    }
    return [
      { qty: 5, tag: "Sample" },
      { qty: 15, tag: "Standard" },
      { qty: 40, tag: "Bulk" },
    ].map((r) => ({ ...r, unit: "MT", total: r.qty * price }));
  }, [unit, price]);

  const goToQuote = (rung) => {
    const p = new URLSearchParams({
      material,
      qty: String(rung.qty),
      unit: rung.unit,
      ...(grade ? { grade } : {}),
      notes: `From Trading Terminal · ${brand} · indicative ${formatINR(price)}/${(unit || "").replace("per ", "")}`,
    });
    nav(`/request-quote?${p.toString()}`);
  };

  return (
    <div className="mt-3" data-testid={`ladder-wrap-${category}`}>
      <button
        data-testid={`ladder-toggle-${category}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-sm border px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-grotesk transition-colors"
        style={{ borderColor: `${color}55`, color, background: open ? `${color}12` : "transparent" }}
      >
        <Layers size={12} /> {open ? "Hide Ladder" : "Ladder Buy"}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="ladder"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`ladder-rungs-${category}`}>
              {rungs.map((r) => (
                <button
                  key={r.tag}
                  data-testid={`ladder-rung-${category}-${r.tag.toLowerCase()}`}
                  onClick={() => goToQuote(r)}
                  className="group text-left rounded-sm border border-white/10 hover:border-current px-2.5 py-2 transition-colors"
                  style={{ color }}
                >
                  <div className="font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] group-hover:text-current transition-colors">
                    {r.tag}
                  </div>
                  <div className="font-serif text-sm text-[#F5F5F0] mt-0.5 leading-none">
                    {r.qty} <span className="text-[9px] text-[#8A8A82] font-body">{r.unit}</span>
                  </div>
                  <div className="text-[9px] mt-1 tabular-nums" style={{ color }}>
                    ≈ {formatINR(r.total)}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-1.5 font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#6f6f68]">
              Tap a rung → prefilled RFQ with the desk
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
